<template>
  <parent>
    <div class="h-100">
      <loading-page v-if="gettingOriginGroup" />

      <form-wrapper
        v-else
        full-width
        full-footer
      >
        <template v-slot:body>
          <div class="origins-form-wrapper">
            <paper-simple class="origins d-md-flex">
              <custom-alert icon-name="Info">
                Create an origin group to provide rerouting during a failover
                event. You can associate an origin group with a cache behavior
                to have requests routed from a primary origin to a secondary
                origin for failover. You must have at least two origins
                specified through an IP address for your distribution before you
                can create an origin group. Please note that with an origin
                group, you can only use GET, HEAD, and OPTIONS HTTP methods in
                your cache behavior.
                <a
                  href="https://docs.captain.gjirafa.tech/cdn/api/originGroups"
                  target="_blank"
                >Learn more</a>
              </custom-alert>
              <div class="col-12 col-lg-8 col-xl-6">
                <validation-observer
                  ref="originGroupRules"
                  tag="form"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Origin Group ID"
                    rules="required"
                  >
                    <input-field
                      label="Origin Group ID"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-input
                        v-model="originGroup.originGroupId"
                        :state="errors.length > 0 ? false : null"
                      />
                    </input-field>
                  </validation-provider>
                </validation-observer>
                <input-field
                  v-if="loaded"
                  label="Routing"
                  label-position="top"
                  info-position="label"
                  class="no-error"
                  :info="messages.origins"
                  @openInfo="
                    showInfo(
                      messages.originRouting.title,
                      messages.originRouting.message,
                    )
                  "
                >
                  <custom-select
                    v-model="originGroup.originRoutingType"
                    :options="getOptions(originGroup.originRoutingTypeOptions)"
                    @change="changeRoutingType"
                  />
                </input-field>

                <input-field
                  v-if="loaded"
                  label="Origins"
                  label-position="top"
                  :info="messages.origins"
                  info-position="label"
                  class="origins__main no-error"
                  @openInfo="
                    showInfo(messages.origins.title, messages.origins.message)
                  "
                >
                  <div class="add">
                    <custom-select
                      v-model="selectedOrigin"
                      class="w-100"
                      :options="originsOptions"
                      :disabled="
                        originsOptions.length < 2 &&
                          selectedOrigins.length === 0
                      "
                    />
                    <b-button
                      variant="bg-white"
                      class="ml-1 large"
                      :disabled="canAdd"
                      @click="onAddOrigin(null)"
                    >
                      Add
                    </b-button>
                  </div>
                </input-field>
                <small
                  v-if="
                    originsOptions.length < 2 && selectedOrigins.length === 0
                  "
                  class="text-danger"
                >There are no valid origins for this distribution.</small>
              </div>
            </paper-simple>

            <div class="origins__main">
              <div class="default-table pt-1">
                <custom-alert
                  icon-name="Info"
                  class="mx-1 mb-0"
                >
                  The weight of an origin is relative to other origins in the
                  origin group. Equal values mean equal weighting. Calculated
                  percentages below are applicable when all origins are healthy.
                </custom-alert>
                <b-table
                  :key="tableKey"
                  ref="originGroup"
                  class="loaded"
                  :items="selectedOrigins"
                  :fields="tableFields"
                  show-empty
                  responsive
                >
                  <template #empty>
                    <no-data no-data-text="No origins have been added yet..." />
                  </template>

                  <template #cell(weight)="data">
                    <b-form-input
                      v-model="selectedOrigins[data.index].weight"
                      type="number"
                      min="0"
                      max="100"
                      :class="error ? 'error' : ''"
                      @change="calculatePercentage"
                    />
                  </template>

                  <template #cell(id)="data">
                    <div class="d-flex">
                      {{ data.value }}
                      <div class="d-flex ml-auto">
                        <span v-if="originGroup.originRoutingType === 0">
                          <a
                            v-if="data.index !== 0"
                            @click="changeIndex(data.index, true)"
                          >
                            <GjIcon name="ArrowUp" />
                          </a>
                          <a
                            v-if="data.index !== selectedOrigins.length - 1"
                            @click="changeIndex(data.index, false)"
                          >
                            <GjIcon name="ArrowDown" />
                          </a>
                        </span>
                        <a @click="onRemoveOrigin(data.item)">
                          <GjIcon name="Close" />
                        </a>
                      </div>
                    </div>
                  </template>
                </b-table>
              </div>
              <p class="text">
                You must add at least two origins for the origin groups.
              </p>
            </div>
          </div>
        </template>

        <template v-slot:footer>
          <b-button
            variant="bg-white"
            @click="$emit('close')"
          >
            Cancel
          </b-button>
          <button-loading
            variant="bg-primary"
            class="ml-16"
            :spinning="details ? updatingOriginGroup : addingOriginGroup"
            @click="validationBehaviorSettings"
          >
            {{ details ? 'Update' : 'Create' }}
          </button-loading></template>
      </form-wrapper>
      <info
        v-if="openInfo"
        :title="infoTitle"
        :message="infoMessage"
        @close="openInfo = false"
      />
    </div>
  </parent>
</template>
<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import messages from '../../../messages'
import redirectError from '@/services/redirectError'

export default {
  name: 'OriginGroupsForm',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    details: {
      type: Boolean,
      default: false,
    },
    originId: {
      type: String,
      default: null,
    },
    distributionId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      messages,
      required,
      selected: null,
      selectedOrigin: null,
      loaded: false,
      error: false,
      watchSelectedOrigins: true,
      origin: {
        distributionId: 0,
        originGroupId: '',
        origins: [],
        originRoutingType: 2,
      },
      originsOptions: [
        {
          value: null,
          text: 'Select',
        },
      ],
      tableFields: [
        {
          key: 'id',
          label: 'Origin ID',
          tdClass: 'full-width d-table-cell',
        },
      ],
      selectedOrigins: [],
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      tableKey: 0,
    }
  },
  computed: {
    canAdd() {
      return this.selectedOrigin === null
    },
    canCreate() {
      return this.selectedOrigins.length > 1
    },
    originGroup() {
      return this.$store.state.cdn.origingroup
    },
    gettingOriginGroup() {
      return this.$store.state.cdn.gettingOriginGroup
    },
    updatingOriginGroup() {
      return this.$store.state.cdn.updatingOriginGroup
    },
    addingOriginGroup() {
      return this.$store.state.cdn.addingOriginGroup
    },
    fullWeight() {
      let weight = 0
      for (let i = 0; i < this.selectedOrigins.length; i++) {
        weight += parseInt(this.selectedOrigins[i].weight, 10)
      }
      return weight === 0 ? 1 : weight
    },
  },
  watch: {
    selectedOrigins() {
      this.error = false
      if (this.watchSelectedOrigins) {
        this.calculatePercentage()
      }
    },
  },
  created() {
    this.originGroup.distributionId = this.distributionId

    this.$store
      .dispatch('cdn/getOriginGroup', {
        distributionId: this.distributionId,
        id: this.details ? this.originId : 'model',
      })
      .then(() => {
        this.fillOriginOptions()
        if (this.details) {
          for (let i = 0; i < this.originGroup.origins.length; i++) {
            this.onAddOrigin(this.originGroup.origins[i])
          }
          this.changeRoutingType()
        } else {
          this.originGroup.distributionId = this.distributionId
        }
        this.loaded = true
      })
      .catch(err => redirectError(err, this.$router, this.$route.path))
  },
  methods: {
    calculatePercentage() {
      for (let i = 0; i < this.selectedOrigins.length; i++) {
        this.selectedOrigins[i].percentage = `${(
          (this.selectedOrigins[i].weight / this.fullWeight)
          * 100
        ).toFixed(2)}%`
      }
    },

    fillOriginOptions() {
      for (let i = 0; i < this.originGroup.originOptions.length; i++) {
        this.originsOptions.push({
          value: this.originGroup.originOptions[i].id,
          text: this.originGroup.originOptions[i].value,
        })
      }
    },

    onAddOrigin(origin) {
      const originId = origin !== null ? origin.id : this.selectedOrigin

      const localOrigin = this.originsOptions.find(
        obj => obj.value === originId,
      )
      const weight = origin !== null ? origin.weight : 1

      this.selectedOrigins.push({
        weight,
        value: localOrigin.value ? localOrigin.value : 0,
        id: localOrigin.text,
        percentage: `${((weight / this.fullWeight) * 100).toFixed(2)}%`,
      })

      this.originsOptions = this.originsOptions.filter(
        obj => obj.value !== originId,
      )
      this.selectedOrigin = null
    },

    onRemoveOrigin(data) {
      this.watchSelectedOrigins = true
      this.selectedOrigins = this.selectedOrigins.filter(
        obj => obj.value !== data.value,
      )
      this.originsOptions.push({
        value: data.value,
        text: data.id,
      })
    },

    changeRoutingType() {
      if (this.originGroup.originRoutingType === 2) {
        this.tableFields = [
          { key: 'weight', variant: 'time' },
          {
            key: 'id',
            label: 'Origin ID',
            tdClass: 'full-width d-table-cell',
          },
          { key: 'percentage', variant: 'time' },
        ]
      } else {
        this.tableFields = [
          {
            key: 'id',
            label: 'Origin ID',
            tdClass: 'full-width d-table-cell',
          },
        ]
      }
    },

    changeIndex(index, up) {
      const secIndex = up ? index - 1 : index + 1

      const el1 = this.selectedOrigins[index]
      const el2 = this.selectedOrigins[secIndex]

      this.selectedOrigins[index] = el2
      this.selectedOrigins[secIndex] = el1

      this.tableKey++
    },

    validationBehaviorSettings() {
      return new Promise((resolve, reject) => {
        this.$refs.originGroupRules.validate().then(success => {
          if (success) {
            this.originGroup.origins = []
            for (let i = 0; i < this.selectedOrigins.length; i++) {
              this.originGroup.origins.push({
                id: this.selectedOrigins[i].value,
                weight:
                  this.originGroup.originRoutingType === 2
                    ? this.selectedOrigins[i].weight
                    : 0,
              })
            }

            const query = {
              id: this.originGroup.id,
              originGroup: {
                distributionId: this.originGroup.distributionId,
                originGroupId: this.originGroup.originGroupId,
                origins: this.originGroup.origins,
                originRoutingType: this.originGroup.originRoutingType,
              },
            }
            this.$emit('submit', query)
          } else {
            reject()
          }
        })
      })
    },

    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
  },
}
</script>

<style lang="scss" scoped>
.no-error::v-deep {
  .errorContainer {
    display: none;
  }
}
.origins__main {
  width: 100%;
  .add {
    max-width: 650px;
    display: flex;
    flex: 1;
    align-items: center;
  }
  .full-width {
    display: flex;
    align-items: center;
    max-width: initial;
  }
}
</style>
